import React from 'react'
import { AlertTriangle } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'

interface ErrorBoundaryProps {
  children: React.ReactNode
  /** Ozel yedek arayuz. Verilmezse standart kurtarma ekrani gosterilir. */
  fallback?: React.ReactNode | undefined
}

interface ErrorBoundaryState {
  error: Error | null
}

/**
 * Yedek arayuz. Ayri bir fonksiyon bilesen cunku sinif bilesenleri hook kullanamaz;
 * metinlerin i18n'den gelmesi gerekiyor.
 */
const ErrorBoundaryFallback: React.FC<{ onReload: () => void }> = ({ onReload }) => {
  const { t } = useLanguage()

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-xl border border-red-200 p-8 text-center shadow-sm">
        <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">{t.errors.renderCrashTitle}</h1>
        <p className="text-sm text-gray-600 mb-6">{t.errors.renderCrashBody}</p>
        <button
          type="button"
          onClick={onReload}
          className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-5 py-2.5 text-white font-medium hover:bg-primary-700 transition-colors"
        >
          {t.errors.reloadPage}
        </button>
      </div>
    </div>
  )
}

/**
 * Render sirasindaki hatalari yakalar.
 *
 * Neden: projede hic hata siniri yoktu -- tek bir render hatasi TUM uygulamayi
 * beyaz ekrana dusuruyordu ve kullanici ne oldugunu anlamiyordu.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[ErrorBoundary]', error, errorInfo.componentStack)
  }

  private handleReload = (): void => {
    window.location.reload()
  }

  override render(): React.ReactNode {
    if (this.state.error) {
      return this.props.fallback ?? <ErrorBoundaryFallback onReload={this.handleReload} />
    }
    return this.props.children
  }
}

export default ErrorBoundary
